import { GridValidRowModel, gridFilteredSortedRowEntriesSelector } from "@mui/x-data-grid-pro";
import { SlateResponse } from "../store/types";
import { isInLineup } from "../../utils/showdownHandler";
import { GridApiCommunity } from "@mui/x-data-grid/internals";
import { LineupApiResponse } from "../../preContest/queries/queryTypes";

export const canFavoriteLineup = (filteredAlreadySavedLineups: string[] , lineup: GridValidRowModel, uniquePlayersCount: string, currPlayerExposure: { [key: string]: number }, currStackExposure: { [key: string]: number }, totalLineups: number, selectedSlate: SlateResponse, playerExposureDict: { [key: string]: number}, stackExposureDict: { [key: string]: number}, currentFavoritedLineups: string[], allowDupes: boolean, hasCaptain: boolean) => {
  const currentPlayerExposures = Object.entries(currPlayerExposure);
  const currentStackExposures = Object.entries(currStackExposure);
  const globalPlayerExposure = playerExposureDict['global'] || 100;
  const globalStackExposure = stackExposureDict['global'] || 100;


  if(uniquePlayersCount) {
    // RE: Check if the lineup is unique enough based on the users input, basically the amount of unique players they want in the lineup
    const isUniqueEnough = checkForUniqueness(lineup, parseInt(uniquePlayersCount), filteredAlreadySavedLineups, hasCaptain);
  
    if(!isUniqueEnough) {
      return false
    }
  }

  const playerExposuresToIndexIfAdded = currentPlayerExposures
  .filter(x => isInLineup(lineup.exportableLineup, x[0], selectedSlate.site, selectedSlate.sport));
  const stackExposuresToIndexIfAdded = currentStackExposures
  .filter(x => lineup.stacks.find(y => ((selectedSlate.sport?.toLowerCase() === 'mlb' && y.count > 2) || (selectedSlate.sport?.toLowerCase() === 'nfl' && y.count > 0)) && y.team === x[0]));

  const playerCanBeAddedWithoutGoingOver = playerExposuresToIndexIfAdded
  .filter(x => {
    return (x[1] + 1) > (Math.min(playerExposureDict[x[0]] === undefined ? 100 : playerExposureDict[x[0]], globalPlayerExposure) * totalLineups / 100)
  }).length > 0 // See if any player exposures would be exceeded

  const stackCanBeAddedWithoutGoingOver = stackExposuresToIndexIfAdded
  .filter(x => ((x[1] + 1) > (Math.min(stackExposureDict[x[0]] === undefined ? 100 : stackExposureDict[x[0]], globalStackExposure) * totalLineups / 100))).length > 0  // See if any stack exposures would be exceeded

  if (playerCanBeAddedWithoutGoingOver || stackCanBeAddedWithoutGoingOver || (!allowDupes && currentFavoritedLineups.includes(lineup.lineupHashCode))) { 
    // Don't allow dupes if we specify not to
      return false;
  }
  return true;
}

const getExposuresKey = (player: string, index: number, selectedSlate: SlateResponse) => {
  if (selectedSlate?.type?.toUpperCase() === 'SHOWDOWN') {
    return index === 0 ? `${player}||CPT` : `${player}||FLEX`;
  }
  return player;
}

export const indexExposures = (lineup: GridValidRowModel, currPlayerExposure: { [key: string]: number }, currStackExposure: { [key: string]: number }, selectedSlate: SlateResponse, ) => {
  const lineupString = lineup.exportableLineup as string;
  lineupString.split(',').forEach((x, i) => {
    const key = getExposuresKey(x, i, selectedSlate);
    currPlayerExposure[key] = (currPlayerExposure[key] || 0) + 1;
  })
  lineup.stacks.forEach(y => {
    if(
      (selectedSlate.sport?.toLowerCase() === 'mlb' && y.count > 2) || (selectedSlate.sport?.toLowerCase() === 'nfl' && y.count > 0)
    ) {
      return currStackExposure[y.team] = (currStackExposure[y.team] || 0) + 1;
    }
    return;
  });
  return { currPlayerExposure, currStackExposure };
}

export const findLineupsToFavorite = (playerExposureDict: { [key: string]: number}, stackExposureDict: { [key: string]: number}, savedLineups:(LineupApiResponse | GridValidRowModel)[], apiRef: React.MutableRefObject<GridApiCommunity>, selectedSlate: SlateResponse, pageCount: number, uniquePlayersCount?: string, allowDupes: boolean = true) => {
  let currentPlayerExposure = initializeCurrentExposureDict(playerExposureDict);
  let currentStackExposure = initializeCurrentExposureDict(stackExposureDict);
  const alreadySavedLineups = savedLineups?.map(x => x.exportableLineup);
  const data = gridFilteredSortedRowEntriesSelector(apiRef)?.map(x => x.model);

  const filteredAlreadySavedLineups = data?.filter(x => !alreadySavedLineups.includes(x.exportableLineup));
  let lineupsToFavorite: GridValidRowModel[] = [];
  let favoritedLineupHashcodes: string[] = [];
  let favoritedLineupsString: string[] = [];
  filteredAlreadySavedLineups.forEach((lineup: GridValidRowModel) => {
    if (canFavoriteLineup(favoritedLineupsString, lineup, uniquePlayersCount || '', currentPlayerExposure, currentStackExposure, pageCount, selectedSlate, playerExposureDict, stackExposureDict, favoritedLineupHashcodes, allowDupes, selectedSlate.type?.toLowerCase() === 'showdown') && lineupsToFavorite.length < pageCount) {
      lineupsToFavorite.push(lineup);
      const { currPlayerExposure, currStackExposure } = indexExposures(lineup, currentPlayerExposure, currentStackExposure, selectedSlate);
      currentPlayerExposure = currPlayerExposure;
      currentStackExposure = currStackExposure;
      favoritedLineupHashcodes.push(lineup.lineupHashCode);
      favoritedLineupsString.push(lineup.exportableLineup);
    }
  });

  const saveableLineups = [
    ...savedLineups,
    ...lineupsToFavorite,
  ]
  return saveableLineups;
}

const initializeCurrentExposureDict = (exposureDict: { [key: string]: number }) => {
  const keys = Object.keys(exposureDict || {});
  const newExposureDict = {};
  keys.forEach(key => {
    newExposureDict[key] = 0;
  });
  return newExposureDict;
}


const checkForUniqueness = (lineup: GridValidRowModel, uniquePlayerPerLineup: number, currentlySavedLineups: string[], hasCaptain: boolean) => {
  let players = lineup.exportableLineup.split(',');
  if(hasCaptain) {
    // If it's a showdown lineup, we need to account for the captain who is always the first player
    players = players.map((x, i) => i === 0 ? `${x}||CPT` : x);
  }

  const doesAnyOtherLineupInvalidateThisOne = currentlySavedLineups.some((x) => {
    let playersInLineup = x.split(',');
    if(hasCaptain) {
      // If it's a showdown lineup, we need to account for the captain who is always the first player
      playersInLineup = playersInLineup.map((x, i) => i === 0 ? `${x}||CPT` : x);
    }

    const uniquePlayers = players.filter(player => !playersInLineup.includes(player));

    return uniquePlayers.length < uniquePlayerPerLineup;
  })

  if(doesAnyOtherLineupInvalidateThisOne) {
    return false;
  }

  return true;
}